// Administrator review of a PR whose SAP posting outcome is unknown
// (SAPPostingStatus 'Unknown' or a 'Posting' left behind). Posting, retry and
// reinitiation stay blocked until an administrator checks SAP and either links
// the SAP Purchase Request that was created or confirms that none exists.
// Server side: backend/src/services/pr-posting-review.service.ts.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Radio, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import apiService from '../../services/api.service';

interface SAPDocument {
  docEntry: number;
  docNum: number;
  docDate: string | null;
  status: string;
  cancelled: boolean;
  prNumber: string;
}

interface PostingReview {
  prId: number;
  prNumber: string;
  status: string;
  postingStatus: string | null;
  postingError: string | null;
  attemptedAt: string | null;
  needsReview: boolean;
  localReference: { docEntry: number; docNum: number | null } | null;
  sap: { available: boolean; reason: string | null; documents: SAPDocument[] };
}

interface Props {
  open: boolean;
  prId: number;
  onClose: () => void;
  /** Called after the review was saved; the PR should be reloaded. */
  onResolved: (message: string) => void;
}

const errorMessage = (error: any, fallback: string): string =>
  String(error?.response?.data?.message || error?.message || fallback);

const PRPostingReviewDialog: React.FC<Props> = ({ open, prId, onClose, onResolved }) => {
  const [review, setReview] = useState<PostingReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [manualDocNum, setManualDocNum] = useState('');
  const [confirmNoDocument, setConfirmNoDocument] = useState(false);
  const [note, setNote] = useState('');
  const request = useRef(0);

  const load = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    setError('');
    try {
      const response = await apiService.getPRPostingReview(prId);
      if (current !== request.current) return;
      const data: PostingReview = response?.data;
      setReview(data);
      const active = (data?.sap?.documents || []).filter((doc) => !doc.cancelled);
      const linked = data?.localReference?.docEntry;
      setSelected(linked && active.some((doc) => doc.docEntry === linked) ? linked : active.length === 1 ? active[0].docEntry : null);
    } catch (e: any) {
      if (current === request.current) setError(errorMessage(e, 'Unable to check SAP for this PR.'));
    } finally {
      if (current === request.current) setLoading(false);
    }
  }, [prId]);

  useEffect(() => {
    if (!open) return;
    setReview(null);
    setSelected(null);
    setManualDocNum('');
    setConfirmNoDocument(false);
    setNote('');
    void load();
    return () => { request.current += 1; };
  }, [open, load]);

  const documents = review?.sap.documents || [];
  const activeDocuments = documents.filter((doc) => !doc.cancelled);
  const searchable = !!review?.sap.available;
  const noteOk = note.trim().length >= 5 && note.trim().length <= 1000;
  const manualNum = Number(manualDocNum);
  const canLink = noteOk && !saving && (searchable ? selected !== null : Number.isSafeInteger(manualNum) && manualNum > 0);
  const canMarkNotPosted = noteOk && !saving && !review?.localReference && activeDocuments.length === 0
    && (searchable || confirmNoDocument);

  const resolve = async (outcome: 'posted' | 'not-posted') => {
    setSaving(true);
    setError('');
    try {
      const response = await apiService.resolvePRPostingReview(prId, {
        outcome,
        note: note.trim(),
        ...(outcome === 'posted'
          ? (searchable ? { docEntry: selected ?? undefined } : { docNum: manualNum })
          : { confirmNoDocument: !searchable && confirmNoDocument }),
      });
      onResolved(String(response?.message || 'SAP posting review saved.'));
    } catch (e: any) {
      setError(errorMessage(e, 'Unable to save the review.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>Review SAP posting{review?.prNumber ? ` — ${review.prNumber}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && !review && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} /><Typography>Checking SAP…</Typography>
          </Stack>
        )}
        {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {review && (
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" color="warning" label={`SAP posting: ${review.postingStatus || '—'}`} />
                <Chip size="small" variant="outlined" label={`PR status: ${review.status || '—'}`} />
                {review.attemptedAt && <Typography variant="body2" color="text.secondary">Last attempt {new Date(review.attemptedAt).toLocaleString()}</Typography>}
              </Stack>
              {review.postingError && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{review.postingError}</Typography>}
              {!review.needsReview && <Alert severity="info" sx={{ mt: 1 }}>This PR no longer needs a review.</Alert>}
              {review.localReference && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This PR already records SAP document {review.localReference.docNum || review.localReference.docEntry}. Link it to finish the posting.
                </Alert>
              )}
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">SAP Purchase Requests for {review.prNumber}</Typography>
                <Button size="small" onClick={() => void load()} disabled={loading || saving}>{loading ? 'Checking…' : 'Check SAP again'}</Button>
              </Stack>
              {searchable ? (
                documents.length === 0 ? (
                  <Alert severity="success" sx={{ mt: 1 }}>SAP has no Purchase Request for this PR number.</Alert>
                ) : (
                  <>
                    {activeDocuments.length > 1 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        SAP has {activeDocuments.length} active Purchase Requests for this PR — a duplicate. Cancel the extra one in SAP, then link the document to keep.
                      </Alert>
                    )}
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Doc No.</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((doc) => (
                            <TableRow key={doc.docEntry} hover={!doc.cancelled} onClick={() => { if (!doc.cancelled) setSelected(doc.docEntry); }} sx={{ cursor: doc.cancelled ? 'default' : 'pointer' }}>
                              <TableCell padding="checkbox">
                                <Radio size="small" checked={selected === doc.docEntry} disabled={doc.cancelled}
                                  inputProps={{ 'aria-label': `Link SAP Purchase Request ${doc.docNum}` }} onChange={() => setSelected(doc.docEntry)} />
                              </TableCell>
                              <TableCell>{doc.docNum}</TableCell>
                              <TableCell>{doc.docDate || '—'}</TableCell>
                              <TableCell>{doc.status}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </>
                )
              ) : (
                <>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {review.sap.reason || 'SAP could not be searched by PR number.'} Check SAP manually (Purchase Requests with this PR number).
                  </Alert>
                  <TextField size="small" sx={{ mt: 1.5 }} label="SAP Purchase Request number (to link)" value={manualDocNum}
                    onChange={(e) => setManualDocNum(e.target.value.replace(/\D/g, '').slice(0, 10))} inputProps={{ inputMode: 'numeric' }} />
                  <FormControlLabel sx={{ display: 'flex', mt: 1 }}
                    control={<Checkbox checked={confirmNoDocument} onChange={(e) => setConfirmNoDocument(e.target.checked)} />}
                    label={`I checked SAP and it has no document for ${review.prNumber}`} />
                </>
              )}
            </Box>

            <TextField label="Review note" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} fullWidth required
              inputProps={{ maxLength: 1000 }} helperText="Kept in the PR's change history (5–1000 characters)." disabled={saving} />
            <Typography variant="body2" color="text.secondary">
              Linking marks the PR as posted to SAP. Marking it as not posted lets it be posted again, or reopened by the initiator.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="warning" variant="outlined" disabled={!review?.needsReview || !canMarkNotPosted} onClick={() => void resolve('not-posted')}>
          Mark as not posted
        </Button>
        <Button variant="contained" disabled={!review?.needsReview || !canLink} onClick={() => void resolve('posted')}>
          {saving ? 'Saving…' : 'Link SAP document'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default PRPostingReviewDialog;
